import { ReactNode } from 'react';
import { Modal } from 'reactstrap';

import XIcon from 'assets/images/x_icon.svg?react';
import { SetIcon } from 'components/icons/SetIcon';
import { uiSurface } from 'modules/ScrAppBookNew/components/ui/uiSurface';
import { media } from 'assets/css/media';

import styled, { css } from 'styled-components';

// Force the design-elements card — the gallery's template otherwise themes .modal-content.
const StyledModal = styled(Modal)<{
  $allowOverflow: boolean;
  $compact: boolean;
  $narrow: boolean;
  $mobileEditLayout: boolean;
}>`
  && {
    max-width: ${({ $compact, $narrow }) => {
      if ($narrow) return '560px';

      return $compact ? '800px' : '1000px';
    }};
    width: 92%;
    margin-left: auto !important;
    margin-right: auto !important;
  }

  && > .modal-content {
    border-radius: 30px !important;
    border: none !important;
    overflow: ${({ $allowOverflow }) => ($allowOverflow ? 'visible' : 'hidden')} !important;
    background: transparent !important;
    color: ${uiSurface.ink} !important;
  }

  @media (max-width: ${media.desktopXl}) {
    ${({ $mobileEditLayout }) =>
      $mobileEditLayout &&
      css`
        min-height: 100%;
        margin-top: 0 !important;
        margin-bottom: 0 !important;
        display: flex;
        align-items: center;
      `}
  }
`;

const Header = styled.div<{ $hideOnMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 30px;
  border-bottom: 1px solid ${uiSurface.border};
  // Deliberately no background: it would be a second painted layer with square corners sitting on
  // the card's rounded ones, and overflow:visible (see $allowOverflow) cannot clip it. The
  // header shows the card through, exactly like the body does.

  svg {
    flex-shrink: 0;
  }

  > svg {
    stroke: currentColor;
  }

  @media (max-width: ${media.desktopXl}) {
    display: ${({ $hideOnMobile }) => ($hideOnMobile ? 'none' : 'flex')};
  }
`;

const Title = styled.span`
  flex: 1;
  font-size: 16px;
  font-weight: 400 !important;
  color: ${uiSurface.ink};
`;

const Card = styled.div<{ $allowOverflow: boolean }>`
  width: 100%;
  overflow: ${({ $allowOverflow }) => ($allowOverflow ? 'visible' : 'hidden')};
  border: none;
  border-radius: 30px;
  background: ${uiSurface.background};
  color: ${uiSurface.ink};
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${uiSurface.ink};
`;

interface IProps {
  isOpen: boolean;
  close: () => void;
  icon: ReactNode;
  title: string;
  dataCy: string;
  children: ReactNode;
  allowOverflow?: boolean;
  compact?: boolean;
  narrow?: boolean;
  hideHeaderOnMobile?: boolean;
}

// New-design selection modal shell: design-elements rounded card + header (icon + title + ✕). Always
// rendered (even closed) so reactstrap tears down its backdrop.
export const SelectionModalCard: React.FC<IProps> = ({
  isOpen,
  close,
  icon,
  title,
  dataCy,
  children,
  allowOverflow = false,
  compact = false,
  narrow = false,
  hideHeaderOnMobile = false
}) => (
  <StyledModal
    $allowOverflow={allowOverflow}
    $compact={compact}
    $narrow={narrow}
    $mobileEditLayout={hideHeaderOnMobile}
    centered
    isOpen={isOpen}
    toggle={close}
    zIndex={999999}
    // opts out of the global .modal-content fill (styles/index.scss) — the Card paints itself
    contentClassName="ui-card-shell"
  >
    <Card $allowOverflow={allowOverflow} data-cy={dataCy}>
      <Header $hideOnMobile={hideHeaderOnMobile}>
        {icon}
        <Title>{title}</Title>
        <CloseButton onClick={close} data-cy={`${dataCy}-close`} type="button">
          <SetIcon slug="close-x" fallback={XIcon} width={22} height={22} />
        </CloseButton>
      </Header>
      {children}
    </Card>
  </StyledModal>
);
